import { Container, Point, Graphics } from 'pixi.js'
import { isArchive, type BulletStyle } from '@/scripts/config/settings'
import { ITEM_BULLET } from '@/scripts/config/constants'
import { createBulletContainer } from './bulletRenderer'
import { createLabelContainer } from './labelRenderer'
import { animateLabelShow, animateLabelHide, animateBulletShow, animateBulletHide } from './bulletAnimations'

export default class ItemBullet extends Container {
  private style: BulletStyle
  private isLeft: boolean
  private isShown: boolean
  private isHovered: boolean
  private shouldBeDisplayed: boolean
  private labelContainer?: Container
  private labelXPos: number = 0
  private bulletContainer?: Container

  constructor(position: Point, title: string, dayLabel: number, sceneWidth: number, style: BulletStyle) {
    super()

    this.x = Math.round(position.x)
    this.y = Math.round(position.y)

    this.style = style
    this.isLeft = this.x > sceneWidth / 2

    // Force specific labels to open to the right to not cover the characters
    if (ITEM_BULLET.FORCE_RIGHT_DAYS.includes(dayLabel)) {
      this.isLeft = false
    }

    // Force specific labels to open to the left to not cover the characters
    if (ITEM_BULLET.FORCE_LEFT_DAYS.includes(dayLabel)) {
      this.isLeft = true
    }

    this.visible = false
    this.isHovered = false
    this.isShown = false
    this.shouldBeDisplayed = false

    this.createLabel(title)
    this.createBullet(dayLabel)

    this.eventMode = 'none'
    this.cursor = 'pointer'
  }

  public setHoverState(doHover: boolean): void {
    if (doHover === this.isHovered) {
      return
    }
    this.isHovered = doHover
    this.showBullet(doHover)
    this.showLabel(doHover)
  }

  public displayBullet(display: boolean): void {
    this.shouldBeDisplayed = display
    this.showBullet(display)
  }

  public getBulletCenterGlobal(): Point {
    // When the circle was drawn: drawCircle(0, 0, radius) => center is at (0,0)
    return this.bulletContainer ? this.bulletContainer.toGlobal(new Point(0, 0)) : this.toGlobal(new Point(0, 0))
  }

  private showLabel(doShow: boolean): void {
    if (!this.labelContainer) return

    if (doShow) {
      animateLabelShow(this.labelContainer, this.labelXPos, this.style)
    } else {
      animateLabelHide(this.labelContainer, this.labelXPos, this.style)
    }
  }

  private createLabel(text: string): void {
    const { container, labelXPos } = createLabelContainer(text, this.isLeft, this.style)
    this.labelContainer = container
    this.labelXPos = labelXPos

    const labelMask: Graphics = new Graphics()
    labelMask.beginFill(0x000000, 1.0)
    if (this.isLeft) {
      labelMask.drawRect(
        -this.style.LABEL_WIDTH,
        -(this.style.LABEL_HEIGHT + 4) / 2,
        this.style.LABEL_WIDTH,
        this.style.LABEL_HEIGHT + 4,
      )
    } else {
      labelMask.drawRect(0, -(this.style.LABEL_HEIGHT + 4) / 2, this.style.LABEL_WIDTH, this.style.LABEL_HEIGHT + 4)
    }
    labelMask.endFill()
    this.addChild(labelMask)
    this.labelContainer.mask = labelMask
    this.addChild(this.labelContainer)
  }

  private createBullet(dayLabel: number): void {
    this.bulletContainer = createBulletContainer(dayLabel, this.isLeft, this.style)
    this.addChild(this.bulletContainer)
  }

  private showBullet(doShow: boolean): void {
    if (isArchive) {
      return
    }
    if (doShow === this.isShown) {
      return
    }

    if (!this.bulletContainer) return

    if (doShow) {
      this.isShown = true
      this.visible = true
      animateBulletShow(this.bulletContainer)
    } else if (!this.shouldBeDisplayed) {
      this.isShown = false
      animateBulletHide(this.bulletContainer, () => {
        this.visible = false
      })
    }
  }
}
